import type { Curriculum, Aufgabe, Uebungsabschnitt } from '../types'
import {
  DUR_TONARTEN,
  KREUZ_REIHENFOLGE,
  B_REIHENFOLGE,
  dominante,
  subdominante,
  vorzeichenText,
} from '../domain/tonarten'

function zufallTonart(seed?: number) {
  const idx = seed === undefined
    ? Math.floor(Math.random() * DUR_TONARTEN.length)
    : seed % DUR_TONARTEN.length
  return DUR_TONARTEN[idx]
}

function zufallTonartOhneFis(seed?: number) {
  const liste = DUR_TONARTEN.filter(t => t.name !== 'F♯')
  const idx = seed === undefined
    ? Math.floor(Math.random() * liste.length)
    : seed % liste.length
  return liste[idx]
}

const UA_VORZEICHEN_ANZAHL: Uebungsabschnitt = {
  id: 'UA-1.1.1',
  name: 'Vorzeichen-Anzahl ablesen',
  typ: 'wahl',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    return {
      uebungsabschnittId: 'UA-1.1.1',
      spec: { typ: 'vorzeichen-anzahl', tonart },
      frage: `Wie viele Vorzeichen hat ${tonart.name}-Dur?`,
      richtigeAntwort: vorzeichenText(tonart),
    } satisfies Aufgabe
  },
}

const UA_TONART_AUS_ANZAHL: Uebungsabschnitt = {
  id: 'UA-1.1.2',
  name: 'Tonart aus Vorzeichen-Anzahl',
  typ: 'wahl',
  generateAufgabe: (seed) => {
    const tonart = zufallTonartOhneFis(seed)
    return {
      uebungsabschnittId: 'UA-1.1.2',
      spec: {
        typ: 'tonart-aus-anzahl',
        accidentals: tonart.accidentals,
        accidentalType: tonart.accidentalType,
      },
      frage: tonart.accidentals === 0
        ? 'Welche Dur-Tonart hat keine Vorzeichen?'
        : `Welche Dur-Tonart hat ${tonart.accidentals} ${tonart.accidentalType === 'sharp' ? '♯' : '♭'}?`,
      richtigeAntwort: tonart.name,
    }
  },
}

const UA_KREUZ_REIHENFOLGE: Uebungsabschnitt = {
  id: 'UA-1.1.3',
  name: 'Kreuz-Reihenfolge nennen',
  typ: 'eingabe',
  generateAufgabe: (seed) => {
    const n = ((seed ?? Math.floor(Math.random() * 7)) % 7) + 1
    return {
      uebungsabschnittId: 'UA-1.1.3',
      spec: { typ: 'vorzeichen-reihenfolge', tonart: DUR_TONARTEN[0], n },
      frage: `Nenne die ersten ${n} Kreuze in der richtigen Reihenfolge.`,
      richtigeAntwort: KREUZ_REIHENFOLGE.slice(0, n).join(', '),
    }
  },
}

const UA_B_REIHENFOLGE: Uebungsabschnitt = {
  id: 'UA-1.1.4',
  name: 'B-Reihenfolge nennen',
  typ: 'eingabe',
  generateAufgabe: (seed) => {
    const n = ((seed ?? Math.floor(Math.random() * 7)) % 7) + 1
    return {
      uebungsabschnittId: 'UA-1.1.4',
      spec: { typ: 'vorzeichen-reihenfolge', tonart: DUR_TONARTEN[0], n },
      frage: `Nenne die ersten ${n} B-Vorzeichen in der richtigen Reihenfolge.`,
      richtigeAntwort: B_REIHENFOLGE.slice(0, n).join(', '),
    }
  },
}

const UA_TONART_IM_KREIS: Uebungsabschnitt = {
  id: 'UA-1.2.1',
  name: 'Tonart im Kreis verorten',
  typ: 'wahl',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    return {
      uebungsabschnittId: 'UA-1.2.1',
      spec: { typ: 'tonart-im-kreis', tonart },
      frage: `Tippe ${tonart.name}-Dur im Quintenzirkel an.`,
      richtigeAntwort: tonart.name,
    }
  },
}

const UA_DOMINANTE: Uebungsabschnitt = {
  id: 'UA-1.2.2',
  name: 'Dominante finden',
  typ: 'wahl',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    const dom = dominante(tonart)
    return {
      uebungsabschnittId: 'UA-1.2.2',
      spec: { typ: 'dominante-finden', tonart },
      frage: `Was ist die Dominante von ${tonart.name}-Dur?`,
      richtigeAntwort: dom.name,
    }
  },
}

const UA_SUBDOMINANTE: Uebungsabschnitt = {
  id: 'UA-1.2.3',
  name: 'Subdominante finden',
  typ: 'wahl',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    const sub = subdominante(tonart)
    return {
      uebungsabschnittId: 'UA-1.2.3',
      spec: { typ: 'subdominante-finden', tonart },
      frage: `Was ist die Subdominante von ${tonart.name}-Dur?`,
      richtigeAntwort: sub.name,
    }
  },
}

const UA_KLANG_TONIKA: Uebungsabschnitt = {
  id: 'UA-1.2.4',
  name: 'Tonart am Klang erkennen',
  typ: 'klang-erkennen',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    return {
      uebungsabschnittId: 'UA-1.2.4',
      spec: { typ: 'klang-erkennen-tonika', tonart },
      frage: 'Höre die Kadenz I-IV-V-I und tippe die Tonart im Kreis an.',
      richtigeAntwort: tonart.name,
    }
  },
}

const UA_TONIKA_SPIELEN: Uebungsabschnitt = {
  id: 'UA-1.2.5',
  name: 'Tonika auf der Klaviatur spielen',
  typ: 'klang-spielen',
  generateAufgabe: (seed) => {
    const tonart = zufallTonart(seed)
    return {
      uebungsabschnittId: 'UA-1.2.5',
      spec: { typ: 'tonika-spielen', tonart },
      frage: `Spiele die Tonika von ${tonart.name}-Dur auf der Klaviatur.`,
      richtigeAntwort: tonart.name,
    }
  },
}

export const QUINTENZIRKEL_CURRICULUM: Curriculum = {
  schulfach: 'musik',
  werk: 'quintenzirkel-und-funktionale-harmonik',
  programme: [
    {
      id: 'P1',
      name: 'Grundlagen',
      niveau: 'unterstufe',
      lerneinheiten: [
        {
          id: 'LE-1.1',
          name: 'Vorzeichen erkennen und benennen',
          beschreibung: 'Anzahl, Reihenfolge und Symbol-Typ der Vorzeichen pro Dur-Tonart.',
          uebungsabschnitte: [
            UA_VORZEICHEN_ANZAHL,
            UA_TONART_AUS_ANZAHL,
            UA_KREUZ_REIHENFOLGE,
            UA_B_REIHENFOLGE,
          ],
        },
        {
          id: 'LE-1.2',
          name: 'Tonarten im Quintenzirkel verorten',
          beschreibung: 'Position, Quint-Beziehung zur Dominante und Subdominante, Klang-Wiedererkennung.',
          uebungsabschnitte: [
            UA_TONART_IM_KREIS,
            UA_DOMINANTE,
            UA_SUBDOMINANTE,
            UA_KLANG_TONIKA,
            UA_TONIKA_SPIELEN,
          ],
        },
      ],
    },
    {
      id: 'P2',
      name: 'Dreiklänge und Kadenzen',
      niveau: 'mittelstufe',
      lerneinheiten: [],
    },
    {
      id: 'P3',
      name: 'Liedbegleitung',
      niveau: 'mittelstufe',
      lerneinheiten: [],
    },
    {
      id: 'P4',
      name: 'Transposition und Modulation',
      niveau: 'oberstufe',
      lerneinheiten: [],
    },
  ],
}

export function alleUebungsabschnitte(): Uebungsabschnitt[] {
  return QUINTENZIRKEL_CURRICULUM.programme.flatMap(p =>
    p.lerneinheiten.flatMap(le => le.uebungsabschnitte),
  )
}

export function findeUebungsabschnitt(id: string): Uebungsabschnitt | undefined {
  return alleUebungsabschnitte().find(ua => ua.id === id)
}
